import { useEffect, useState } from 'react';
import { useStation } from './lib/useStation';
import { useCoder } from './lib/useCoder';
import { useAudio } from './lib/useAudio';
import { usePrefetch } from './lib/usePrefetch';
import { useOffline } from './lib/useOffline';
import { useGeo } from './lib/useGeo';
import { api } from './lib/api';
import { Player } from './views/Player';
import { Settings } from './views/Settings';
import { Login } from './views/Login';

type View = 'player' | 'settings';

export default function App() {
  const [gate, setGate] = useState<'checking' | 'ok' | 'needed'>('checking');
  useEffect(() => {
    api.checkAuth().then((ok) => setGate(ok ? 'ok' : 'needed'));
  }, []);

  if (gate === 'checking')
    return (
      <div className="dot-grid flex h-dvh items-center justify-center">
        <div className="animate-pulse text-2xl font-bold tracking-[0.2em] text-fg/50">Claudio</div>
      </div>
    );
  if (gate === 'needed') return <Login onSuccess={() => setGate('ok')} />;
  return <AppShell />;
}

function AppShell() {
  const { snap: serverSnap, connected, messages, historyLoaded, pushUser, working, statusLabel } = useStation();
  const off = useOffline(serverSnap, connected); // offline fallback: local advance + sync-later
  const snap = off.snap;
  const audio = useAudio(snap, () => off.localAdvance('ended')); // single, app-lifetime audio element (never two songs)
  off.elapsedRef.current = () => audio.currentTime;
  usePrefetch(snap); // keep current + next songs fully on-device so dropouts cost nothing
  useGeo();
  const coder = useCoder(); // code-mode lives at app scope so the transcript survives view changes

  const [view, setViewState] = useState<View>(location.hash === '#settings' ? 'settings' : 'player');
  const [mode, setMode] = useState<'dj' | 'code'>('dj');
  const setView = (v: View) => {
    setViewState(v);
    history.replaceState(null, '', v === 'player' ? '#' : `#${v}`);
  };

  // Settings opens from the native macOS menu (⌘,)
  useEffect(() => {
    const c = (window as { claudio?: { onOpenSettings?: (cb: () => void) => void } }).claudio;
    c?.onOpenSettings?.(() => setView('settings'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view !== 'settings') return;
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || e.keyCode === 229) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      setView('player');
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const isElectron = !!(window as any).claudio?.isElectron;

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      {isElectron && <div className="drag fixed inset-x-0 top-0 z-[80] h-6" />}

      <main className="min-h-0 flex-1 overflow-hidden">
        {view === 'player' && (
          <Player
            snap={snap}
            messages={messages}
            historyLoaded={historyLoaded}
            pushUser={pushUser}
            working={working}
            statusLabel={statusLabel}
            connected={connected}
            coder={coder}
            mode={mode}
            setMode={setMode}
            audio={audio}
            offline={off.active}
            onOfflineNext={() => void off.localAdvance('skip')}
            onOpenSettings={() => setView('settings')}
          />
        )}
        {view === 'settings' && (
          <div className="h-full overflow-hidden">
            <button
              onClick={() => setView('player')}
              className="no-drag fixed left-4 top-[max(1.75rem,env(safe-area-inset-top))] z-[70] py-2 text-xs tracking-[0.2em] text-muted hover:text-fg"
            >
              ← BACK
            </button>
            <Settings />
          </div>
        )}
      </main>
    </div>
  );
}
